import { defineNuxtPlugin, useRuntimeConfig, useRouter } from '#app';

// Loads Google Analytics 4 (gtag.js) and Yandex.Metrika (tag.js) on the
// client after mount. Page-view tracking is handled natively by both
// libraries; our useAnalytics composable wires in custom conversion
// events (WhatsApp CTA click, email CTA click, hero search submit).

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    ym?: (id: number, action: string, ...args: unknown[]) => void;
  }
}

export default defineNuxtPlugin(() => {
  // Skip in dev so local traffic doesn't pollute the dashboards.
  if (!import.meta.env.PROD) return;

  // Skip on operator/closed routes — see utils/analytics-suppressed.ts.
  if (isAnalyticsSuppressed(location.pathname)) return;

  const { ga4Id, yandexMetrikaId, yandexMetrikaIdRu } =
    useRuntimeConfig().public as {
      ga4Id?: string;
      yandexMetrikaId?: string;
      yandexMetrikaIdRu?: string;
    };

  // GA4 is unified across both domains; Yandex still splits by domain so
  // .ru traffic flows into its own property and stays separated from .ge.
  const isRu = location.hostname.endsWith('georgiadream.ru');
  const activeGa4Id = ga4Id;
  const activeYmId = isRu ? yandexMetrikaIdRu : yandexMetrikaId;

  // Stub queues installed synchronously so useAnalytics().trackEvent calls
  // before the real libraries finish loading don't get lost. gtag.js and
  // tag.js both replay the queue on init.
  window.dataLayer = window.dataLayer || [];
  // Must push the Arguments object (not a real Array via spread/rest) —
  // gtag.js silently ignores queue entries that aren't shaped like
  // Arguments, so `function gtag(...a){dataLayer.push(a)}` looks fine
  // but never fires page_view. Stay verbatim with Google's snippet.
  // Rest param is type-only (unused): the body must push `arguments` verbatim.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    (window.dataLayer as unknown[]).push(arguments);
  }
  (window as unknown as { gtag: typeof gtag }).gtag = gtag;
  if (activeGa4Id) {
    gtag('js', new Date());
    gtag('config', activeGa4Id);

    // Boot-time suppression (above) only covers the landing route. On
    // client-side SPA navigation this plugin never re-runs, so once gtag.js
    // is loaded GA4's History-API auto-page_view keeps tracking /admin/*
    // (the operator reaches admin via in-app links, not a fresh page load —
    // 54 /admin/bookings views in the May GA4 data were exactly this).
    // Toggle GA4's official `ga-disable-<id>` opt-out flag per route. Set in
    // beforeEach so it lands before Vue Router's history.pushState reaches
    // gtag's history listener; flips back to false on return to public routes.
    const disableKey = `ga-disable-${activeGa4Id}`;
    useRouter().beforeEach((to) => {
      (window as unknown as Record<string, boolean>)[disableKey] =
        isAnalyticsSuppressed(to.path);
    });
  }

  if (activeYmId) {
    window.ym =
      window.ym ||
      function (...args: unknown[]) {
        ((window.ym as unknown as { a?: unknown[] }).a =
          (window.ym as unknown as { a?: unknown[] }).a || []).push(args);
      };
    (window.ym as unknown as { l?: number }).l = Date.now();
    // webvisor: true со 2026-05-26 — recording возвращён, но tag.js теперь
    // грузится только после первого user interaction (см. scheduleYm ниже),
    // так что main-thread cost Webvisor'а не падает на LCP-окно. Bouncers
    // без скролла/клика в YM не попадут (GA4 их продолжает считать через
    // eager-инжект). История: 2026-05-04 webvisor был выключен ради TBT
    // (~810 ms на /, ~700 ms среднее apt) при синхронной загрузке tag.js.
    window.ym(Number(activeYmId), 'init', {
      ssr: true,
      webvisor: true,
      clickmap: true,
      ecommerce: 'dataLayer',
      referrer: document.referrer,
      url: location.href,
      accurateTrackBounce: true,
      trackLinks: true,
    });
  }

  // GA4 — eager defer: rIC после load события. tag.js (~80 KiB) лёгкий и
  // не открывает WSS, так что TBT-импакт пренебрежимо мал даже на bouncer'ах.
  // History: 2026-05-03 PSI показал TBT ~510 ms с rIC-only — rIC срабатывал
  // в LCP-окне на Slow 4G; move в load+rIC решило. setTimeout для Safari.
  const injectGa4 = () => {
    if (!activeGa4Id) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${activeGa4Id}`;
    document.head.appendChild(s);
  };

  const injectYm = () => {
    if (!activeYmId) return;
    const src = `https://mc.yandex.ru/metrika/tag.js?id=${activeYmId}`;
    if (Array.from(document.scripts).some((scr) => scr.src === src)) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    document.head.appendChild(s);
  };

  const scheduleGa4 = () => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof ric === 'function') ric(injectGa4, { timeout: 4000 });
    else setTimeout(injectGa4, 2500);
  };

  // YM lazy-load: tag.js + Webvisor recorder (~200 KiB, WSS connection,
  // постоянная main-thread работа) грузятся только после первого
  // взаимодействия — скролл/клик/touch/key. Safety-net 15 s ловит visitors
  // которые читают hero без скролла; без него «тихие» сессии терялись бы.
  // Trade-off: pure bouncers (close без interaction) не попадают в YM-визиты —
  // их продолжает считать GA4. YM-метрика «Визиты» из-за этого станет ниже
  // исторической; не сравнивать яблоко с яблоком до/после 2026-05-26.
  const scheduleYm = () => {
    let fired = false;
    const events = ['scroll', 'click', 'keydown', 'touchstart', 'pointerdown'] as const;
    const opts = { capture: true } as const;
    const fire = () => {
      if (fired) return;
      fired = true;
      events.forEach((e) => window.removeEventListener(e, fire, opts));
      clearTimeout(timeoutId);
      injectYm();
    };
    events.forEach((e) =>
      window.addEventListener(e, fire, { once: true, passive: true, capture: true }),
    );
    const timeoutId = setTimeout(fire, 15000);
  };

  const start = () => { scheduleGa4(); scheduleYm(); };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
});
